import { AuthAPIError, CEREMONY_FAILURE_CODES } from "./api";

/** What adding a passkey shows while Starling fetches the one-time
 * challenge, before the device's own prompt opens: the prompt is not
 * promised until the browser is about to show it. */
export const PASSKEY_PREPARING_WORDS = {
  title: "Getting your device ready",
  body: "Your device asks you to confirm in a moment.",
} as const;

/** What adding a passkey says once single sign-on confirms in its own
 * window: the browser opens the device's prompt only from a press, so the
 * person presses Create passkey once more, and focus waits on that button. */
export const PASSKEY_SSO_CONFIRMED_WORDS =
  "Single sign-on confirmed it's you. Press Create passkey once more: your browser opens the device's prompt only from a press.";

/** Why a passkey ceremony stopped, in the terms a person can act on. */
export type PasskeyFailure =
  | "cancelled"
  | "already-on-device"
  | "already-enrolled"
  | "wrong-address"
  | "unavailable"
  | "refused"
  | "session-ended"
  | "other";

const KIND_BY_CODE: Readonly<Record<string, PasskeyFailure>> = {
  PASSKEY_CANCELLED: "cancelled",
  PASSKEY_ALREADY_ON_DEVICE: "already-on-device",
  PASSKEY_WRONG_ADDRESS: "wrong-address",
  PASSKEY_UNAVAILABLE: "unavailable",
};

/** Reads a failure from the browser ceremony or the server into one kind.
 * The browser's own messages are never shown: they name WebAuthn internals
 * ("relying party ID", ".well-known/webauthn") nobody can act on. */
export function passkeyFailure(err: unknown): PasskeyFailure {
  if (err instanceof DOMException) {
    return KIND_BY_CODE[CEREMONY_FAILURE_CODES[err.name] ?? ""] ?? "other";
  }
  if (!(err instanceof AuthAPIError)) return "other";
  const kind = KIND_BY_CODE[err.code];
  if (kind) return kind;
  if (err.status === 409) return "already-enrolled";
  if (err.status === 422) return "refused";
  if (err.status === 401) return "session-ended";
  return "other";
}

/** What to say when adding a passkey stops. Every line says whether anything
 * was added; none of them ends the session, and none says what to do next —
 * the screen showing it knows that. */
export const PASSKEY_ADD_WORDS: Record<PasskeyFailure, string> = {
  cancelled: "Your device cancelled the request. Nothing was added.",
  "already-on-device":
    "Your device says it already holds a passkey for this account. Nothing was added.",
  "already-enrolled":
    "That passkey is already enrolled in Starling. Nothing was added.",
  "wrong-address":
    "Passkeys aren't set up for this address yet, so nothing was added. Tell your administrator.",
  unavailable:
    "This browser can't create passkeys, so nothing was added. A current browser on a phone or computer with a screen lock can.",
  refused:
    "Starling couldn't verify that passkey. Nothing was added, and you're still signed in.",
  "session-ended":
    "Your session ended. Sign in again; if the passkey was saved, it's listed in Settings › Security.",
  other: "Starling couldn't save the passkey. Nothing was added.",
};

/** What to say when signing in with a passkey stops. */
export const PASSKEY_SIGN_IN_WORDS: Record<PasskeyFailure, string> = {
  cancelled:
    "Your device cancelled the request. Try again, or continue with email.",
  "already-on-device":
    "That passkey couldn't sign you in. Try again, or continue with email.",
  "already-enrolled":
    "That passkey couldn't sign you in. Try again, or continue with email.",
  "wrong-address":
    "Passkeys aren't set up for this address yet. Continue with email, and tell your administrator.",
  unavailable:
    "Passkeys aren't available in this browser. Continue with email instead.",
  refused:
    "Starling didn't recognise that passkey. Try another, or continue with email.",
  "session-ended":
    "Starling didn't recognise that passkey. Try another, or continue with email.",
  other: "Passkey sign-in didn't finish. Try again, or continue with email.",
};

/** Whether trying the same passkey again could work. */
export function passkeyRetryable(failure: PasskeyFailure): boolean {
  return (
    failure === "cancelled" || failure === "refused" || failure === "other"
  );
}
